/**
 * useInstructionCatalogue — the instruction-catalogue seam the Instructions step
 * consumes.
 *
 * As of A1.2 this is a THIN ADAPTER over one v2 call:
 *   `dataQueries/purchaseFlowRevampInstructions.query.ts` →
 *   `POST /workspace/{ws}/sessions/{sessionId}/instructions`
 * It replaced the legacy Path-A shim (mint a LEGACY session via
 * `getCategoryConfig`, then POST legacy `/order/configuration/instruction`).
 *
 * WHY the name / location / return shape did not change (adapter-in-place,
 * PM-D1): `resolveMeetingModes(organizationInstructions, status, selectedMode)`
 * is typed against a ProductKind-keyed map + this exact `status` vocabulary, and
 * its 10 DF-I9 tests build fixtures that way. Keeping both means
 * `meeting.helpers.ts` and its tests need ZERO edits — DF-I9 semantics are
 * preserved by construction rather than re-derived. Only the option VALUE type
 * changed (legacy `InstructionOptionDTO` → v2 `ResolvedInstructionOption`),
 * because the legacy shape's `id`/`isPrimary`/`price`/`feePrice` simply do not
 * exist on v2 and faking them is the shim rot A1.2 removes.
 *
 * What the v2 response newly unlocks, all additive on the return shape:
 *   - `billingOptions` → catalogue-driven payment methods,
 *   - `visible`        → the EXACT step-skip signal,
 *   - `timezone`       → shoot-location tz (same field name, zero UI work),
 *   - per-option `price` → real extras prices instead of the legacy fee pair.
 *
 * MODULE-CONTRACT §11: data-only React Query seam, no legacy provider tree, no
 * store writes. `Instructions.page` owns the one sanctioned mount-time write
 * (`setExtrasFromInstructions`, §11.2).
 */

import { useMemo } from 'react'

import { bucketInstructionOptions, useInstructionOptions } from 'dataQueries/purchaseFlowRevampInstructions.query'

import type { UseInstructionCatalogueResult } from './useInstructionCatalogue.types'

export type { InstructionCatalogueStatus, UseInstructionCatalogueResult } from './useInstructionCatalogue.types'

/**
 * Fetches the instruction catalogue for the current session + cart.
 *
 * @example
 * const { organizationInstructions, extraServices, billingOptions, timezone, visible, status } =
 *   useInstructionCatalogue()
 */
export function useInstructionCatalogue(): UseInstructionCatalogueResult {
  const { data, status, error, refetch } = useInstructionOptions()

  const buckets = useMemo(() => bucketInstructionOptions(data), [data])

  return {
    organizationInstructions: buckets.organization,
    extraServices: buckets.extras,
    billingOptions: buckets.billingOptions,
    ctBookingOptions: buckets.ctBookingOptions,
    timezone: data?.timezone,
    currency: buckets.currency,
    visible: data?.visible,
    status,
    error,
    refetch,
  }
}
